import React, { useEffect, useMemo, useRef } from 'react';
import { FixedSizeList as List } from 'react-window';

const ROW_HEIGHT = 28;
const HEADER_HEIGHT = 36;
const FILTER_HEIGHT = 32;
// Reserve space at the bottom so the horizontal scrollbar is always visible
// within the window and never overlaps the last data row.
const SCROLLBAR_GUTTER = 18;
const INDEX_COL_WIDTH = 56;
const MIN_COL_WIDTH = 100;
const MAX_COL_WIDTH = 360;

function estimateColWidth(name, sampleValues) {
  const headerLen = String(name).length;
  let maxLen = headerLen;
  for (let i = 0; i < Math.min(50, sampleValues.length); i += 1) {
    const v = sampleValues[i];
    if (v == null) continue;
    const len = String(v).length;
    if (len > maxLen) maxLen = len;
  }
  const px = Math.round(maxLen * 7.5 + 24);
  return Math.max(MIN_COL_WIDTH, Math.min(MAX_COL_WIDTH, px));
}

const NullCell = () => <span className="db-explorer-null">NULL</span>;

const DataGrid = ({
  columns,
  rows,
  filters,
  sort,
  loading,
  onSort,
  onFilter,
  offset,
}) => {
  const containerRef = useRef(null);
  const [size, setSize] = React.useState({ width: 800, height: 500 });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const update = () => {
      setSize({ width: el.clientWidth, height: el.clientHeight });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const colWidths = useMemo(() => {
    return columns.map((c, ci) => estimateColWidth(c, rows.map((r) => r[ci])));
  }, [columns, rows]);

  const totalWidth = INDEX_COL_WIDTH + colWidths.reduce((a, b) => a + b, 0);

  const Row = ({ index, style }) => {
    const row = rows[index];
    const rowNum = offset + index + 1;
    return (
      <div className="db-explorer-row" style={{ ...style, width: totalWidth }}>
        <div className="db-explorer-cell db-explorer-index" style={{ width: INDEX_COL_WIDTH }}>
          {rowNum}
        </div>
        {columns.map((col, ci) => {
          const v = row[ci];
          return (
            <div
              key={col}
              className="db-explorer-cell"
              style={{ width: colWidths[ci] }}
              title={v == null ? 'NULL' : String(v)}
            >
              {v == null ? <NullCell /> : String(v)}
            </div>
          );
        })}
      </div>
    );
  };

  const listHeight = Math.max(0, size.height - HEADER_HEIGHT - FILTER_HEIGHT - SCROLLBAR_GUTTER);

  return (
    <div ref={containerRef} className="db-explorer-grid">
      <div className="db-explorer-scroll" style={{ width: '100%' }}>
        <div style={{ minWidth: totalWidth }}>
          <div className="db-explorer-header-row" style={{ width: totalWidth, height: HEADER_HEIGHT }}>
            <div className="db-explorer-header-cell db-explorer-index" style={{ width: INDEX_COL_WIDTH }}>#</div>
            {columns.map((col, ci) => {
              const isSorted = sort.column === col;
              const arrow = isSorted ? (sort.direction === 'asc' ? ' ▲' : ' ▼') : '';
              return (
                <div
                  key={col}
                  className={`db-explorer-header-cell${isSorted ? ' is-sorted' : ''}`}
                  style={{ width: colWidths[ci] }}
                  onClick={() => onSort(col)}
                  title={`Click to sort by ${col}`}
                >
                  <span className="db-explorer-header-text">{col}</span>
                  <span className="db-explorer-header-arrow">{arrow}</span>
                </div>
              );
            })}
          </div>

          <div className="db-explorer-filter-row" style={{ width: totalWidth, height: FILTER_HEIGHT }}>
            <div className="db-explorer-filter-cell db-explorer-index" style={{ width: INDEX_COL_WIDTH }} />
            {columns.map((col, ci) => (
              <div key={col} className="db-explorer-filter-cell" style={{ width: colWidths[ci] }}>
                <input
                  type="text"
                  className="db-explorer-filter-input"
                  placeholder="Filter"
                  value={filters[col] || ''}
                  onChange={(e) => onFilter(col, e.target.value)}
                />
              </div>
            ))}
          </div>

          {loading && rows.length === 0 ? (
            <div className="db-explorer-empty">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="db-explorer-empty">No rows.</div>
          ) : (
            <List
              height={listHeight}
              itemCount={rows.length}
              itemSize={ROW_HEIGHT}
              width={Math.max(totalWidth, size.width)}
              overscanCount={8}
            >
              {Row}
            </List>
          )}
        </div>
      </div>
    </div>
  );
};

export default DataGrid;
